import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, DatePicker, Input, Segmented, Select, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Download, Search } from 'lucide-react';
import Layout from '../../Layout';
import { useKho } from './khoStore';
import {
  fmtMoney, fmtNum, fmtQty, itemById, serviceById, serviceUsage, traceLot, whById, xntReport, type Lot, type Move, type ServiceUsage, type XntRow,
} from './khoLogic';
import { ExpiryText, ItemCell, PageHeader, Panel, docUrl, exportXlsx, fmtDate, fmtDateTime } from './khoUi';

type Tab = 'xnt' | 'usage' | 'trace';

/** Báo cáo kho: xuất – nhập – tồn, tiêu hao theo dịch vụ, truy xuất lô */
export default function ReportPage() {
  const s = useKho();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'xnt';
  const from = params.get('from') ? dayjs(params.get('from')) : dayjs().startOf('month');
  const to = params.get('to') ? dayjs(params.get('to')) : dayjs();
  const wh = params.get('wh') || undefined;
  const q = params.get('q') || '';
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const fromMs = from.startOf('day').valueOf();
  const toMs = to.endOf('day').valueOf();
  const now = Date.now();

  const xnt = useMemo(() => xntReport(s, fromMs, toMs, wh), [s, fromMs, toMs, wh]);
  const usage = useMemo(() => serviceUsage(s, fromMs, toMs), [s, fromMs, toMs]);
  const trace = useMemo(() => {
    const t = traceLot(s, q);
    return { ...t, used: t.used.map((m, i) => ({ ...m, key: `${m.docId}-${m.lotNo}-${i}` })) };
  }, [s, q]);

  const sum = (k: keyof XntRow) => xnt.reduce((t, r) => t + (r[k] as number), 0);

  const xntColumns: ColumnsType<XntRow> = [
    { title: 'Mặt hàng', key: 'name', fixed: 'left', width: 260, render: (_, r) => <ItemCell item={r.item} /> },
    { title: 'Đơn vị', key: 'unit', width: 80, render: (_, r) => r.item.unit },
    { title: 'Tồn đầu kỳ', key: 'o', align: 'right', render: (_, r) => fmtNum(r.opening) },
    { title: 'Nhập', key: 'i', align: 'right', render: (_, r) => (r.inQty ? <span className="text-emerald-600">+{fmtNum(r.inQty)}</span> : '—') },
    { title: 'Xuất', key: 'x', align: 'right', render: (_, r) => (r.outQty ? <span className="text-rose-600">−{fmtNum(r.outQty)}</span> : '—') },
    { title: 'Tồn cuối kỳ', key: 'c', align: 'right', render: (_, r) => <b>{fmtNum(r.closing)}</b> },
    { title: 'Tiền nhập', key: 'iv', align: 'right', render: (_, r) => fmtMoney(r.inValue) },
    { title: 'Tiền xuất', key: 'ov', align: 'right', render: (_, r) => fmtMoney(r.outValue) },
    { title: 'Giá trị tồn cuối', key: 'cv', align: 'right', render: (_, r) => <b>{fmtMoney(r.closingValue)}</b> },
  ];

  const usageColumns: ColumnsType<ServiceUsage> = [
    { title: 'Dịch vụ', key: 'name', render: (_, u) => <Link to={`/service-norms?service=${u.service.id}`} className="font-medium text-gray-900 dark:text-gray-100 hover:text-[#3e91ab]">{u.service.name}</Link> },
    { title: 'Số buổi', dataIndex: 'sessions', key: 'n', align: 'right', width: 90 },
    { title: 'Chi phí định mức', key: 'nc', align: 'right', render: (_, u) => fmtMoney(u.normCost) },
    { title: 'Chi phí thực tế', key: 'ac', align: 'right', render: (_, u) => <b>{fmtMoney(u.actualCost)}</b> },
    {
      title: 'Chênh lệch', key: 'd', align: 'right', render: (_, u) => {
        const d = u.actualCost - u.normCost;
        const pct = u.normCost ? Math.round((d / u.normCost) * 100) : 0;
        return <span className={pct > 10 ? 'text-rose-600 font-semibold' : pct > 0 ? 'text-amber-600' : 'text-emerald-600'}>{d > 0 ? '+' : ''}{fmtMoney(d)} ({pct > 0 ? '+' : ''}{pct}%)</span>;
      },
    },
    { title: 'Vật tư / doanh thu', key: 'pct', align: 'right', width: 140, render: (_, u) => `${u.service.price ? Math.round((u.actualCost / (u.service.price * u.sessions)) * 100) : 0}%` },
  ];

  const usedColumns: ColumnsType<Move & { key: string }> = [
    { title: 'Ngày', key: 'at', width: 140, render: (_, m) => fmtDateTime(m.at) },
    { title: 'Khách hàng', key: 'c', render: (_, m) => (m.customer?.phone
      ? <Link to={`/customer-demo/detail?phone=${encodeURIComponent(m.customer.phone)}`} className="text-[#3e91ab]">{m.customer.name}<div className="text-[11px] text-gray-400">{m.customer.phone}</div></Link>
      : m.customer?.name || 'Khách lẻ') },
    { title: 'Mặt hàng', key: 'it', render: (_, m) => itemById(s, m.itemId)?.name },
    { title: 'Lô', dataIndex: 'lotNo', key: 'lot', width: 110, render: v => <span className="font-mono text-xs">{v}</span> },
    { title: 'Dịch vụ', key: 'sv', render: (_, m) => (m.serviceId ? serviceById(s, m.serviceId)?.name : 'Bán lẻ') },
    { title: 'Người thực hiện', dataIndex: 'practitioner', key: 'p', render: v => v || '—' },
    { title: 'SL', key: 'q', align: 'right', width: 90, render: (_, m) => `${fmtNum(-m.qty)} ${itemById(s, m.itemId)?.unit || ''}` },
    { title: 'Phiếu', key: 'doc', width: 130, render: (_, m) => <Link to={docUrl(m.docId)} className="text-[#3e91ab]">{m.code}</Link> },
  ];

  const doExport = () => {
    if (tab === 'xnt') {
      exportXlsx('xuat_nhap_ton', 'XNT', xnt.map(r => ({
        'Mã': r.item.code, 'Tên': r.item.name, 'Đơn vị': r.item.unit, 'Tồn đầu': r.opening, 'Nhập': r.inQty, 'Xuất': r.outQty, 'Tồn cuối': r.closing,
        'Tiền nhập': Math.round(r.inValue), 'Tiền xuất': Math.round(r.outValue), 'Giá trị tồn cuối': Math.round(r.closingValue),
      })));
    } else if (tab === 'usage') {
      exportXlsx('tieu_hao_dich_vu', 'Tiêu hao', usage.map(u => ({
        'Dịch vụ': u.service.name, 'Số buổi': u.sessions, 'Chi phí định mức': Math.round(u.normCost), 'Chi phí thực tế': Math.round(u.actualCost), 'Chênh lệch': Math.round(u.actualCost - u.normCost),
      })));
    } else {
      exportXlsx('truy_xuat_lo', 'Truy xuất', trace.used.map(m => ({
        'Ngày': fmtDate(m.at), 'Khách hàng': m.customer?.name || '', 'SĐT': m.customer?.phone || '', 'Mặt hàng': itemById(s, m.itemId)?.name || '', 'Lô': m.lotNo,
        'Dịch vụ': serviceById(s, m.serviceId)?.name || 'Bán lẻ', 'Người thực hiện': m.practitioner || '', 'Số lượng': -m.qty, 'Phiếu': m.code,
      })));
    }
  };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Báo cáo kho" desc="Xuất – nhập – tồn, tiêu hao vật tư theo dịch vụ và truy xuất lô thuốc đã dùng cho khách"
          extra={<Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>} />

        <div className="flex flex-wrap items-center gap-2">
          <Segmented value={tab} onChange={v => go({ tab: v === 'xnt' ? null : String(v) })}
            options={[{ value: 'xnt', label: 'Xuất nhập tồn' }, { value: 'usage', label: 'Tiêu hao theo dịch vụ' }, { value: 'trace', label: 'Truy xuất lô' }]} />
          {tab !== 'trace' && (
            <DatePicker.RangePicker allowClear={false} format="DD/MM/YYYY" value={[from, to]}
              onChange={v => v && v[0] && v[1] && go({ from: v[0].format('YYYY-MM-DD'), to: v[1].format('YYYY-MM-DD') })}
              presets={[
                { label: 'Tháng này', value: [dayjs().startOf('month'), dayjs()] },
                { label: 'Tháng trước', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                { label: '30 ngày qua', value: [dayjs().subtract(30, 'day'), dayjs()] },
                { label: '90 ngày qua', value: [dayjs().subtract(90, 'day'), dayjs()] },
              ]} />
          )}
          {tab === 'xnt' && <Select allowClear className="w-[220px]" placeholder="Tất cả kho" value={wh} onChange={v => go({ wh: v || null })} options={s.warehouses.map(w => ({ value: w.id, label: w.name }))} />}
          {tab === 'trace' && <Input allowClear autoFocus className="!w-full sm:!w-80" prefix={<Search size={14} className="text-gray-400" />} placeholder="Nhập số lô, mã hoặc tên thuốc (VD: VL-2309, botox)" value={q} onChange={e => go({ q: e.target.value || null })} />}
        </div>

        {tab === 'xnt' && (
          <Panel title={`Xuất nhập tồn ${from.format('DD/MM/YYYY')} – ${to.format('DD/MM/YYYY')}${wh ? ` · ${whById(s, wh)?.name}` : ' · tất cả kho'}`}
            extra={!wh && <span className="text-[11px] text-gray-400">Tất cả kho: chuyển kho nội bộ tính cả nhập và xuất</span>}>
            <Table<XntRow> rowKey={r => r.item.id} size="small" columns={xntColumns} dataSource={xnt} pagination={false} scroll={{ x: 1150 }}
              summary={() => (
                <Table.Summary.Row className="font-semibold bg-gray-50 dark:bg-gray-800">
                  <Table.Summary.Cell index={0} colSpan={6}>Tổng cộng ({xnt.length} mặt hàng)</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">{fmtMoney(sum('inValue'))}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">{fmtMoney(sum('outValue'))}</Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">{fmtMoney(sum('closingValue'))}</Table.Summary.Cell>
                </Table.Summary.Row>
              )} />
          </Panel>
        )}

        {tab === 'usage' && (
          <Panel title="Chi phí vật tư theo dịch vụ" extra={<span className="text-[11px] text-gray-400">Bấm dòng để xem chi tiết vật tư và người thực hiện</span>}>
            <Table<ServiceUsage> rowKey={u => u.service.id} size="middle" columns={usageColumns} dataSource={usage} pagination={false} scroll={{ x: 900 }}
              locale={{ emptyText: 'Không có buổi dịch vụ nào trong khoảng này' }}
              expandable={{
                expandRowByClick: true,
                expandedRowRender: u => (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-2">
                    <div>
                      <div className="text-xs font-semibold text-gray-500 mb-1">Vật tư (thực tế / định mức)</div>
                      {u.items.map(x => {
                        const item = itemById(s, x.itemId);
                        const pct = x.norm ? Math.round(((x.actual - x.norm) / x.norm) * 100) : null;
                        return (
                          <div key={x.itemId} className="flex justify-between text-sm py-0.5 gap-2">
                            <span className="truncate">{item?.name}</span>
                            <span className={pct === null || pct > 10 ? 'text-rose-600 font-medium' : 'text-gray-600'}>
                              {fmtNum(x.actual)} / {fmtNum(x.norm)} {item?.unit}{pct === null ? ' (ngoài định mức)' : pct ? ` (${pct > 0 ? '+' : ''}${pct}%)` : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 mb-1">Theo người thực hiện</div>
                      {u.byPractitioner.map(p => {
                        const pct = p.normCost ? Math.round(((p.actualCost - p.normCost) / p.normCost) * 100) : 0;
                        return (
                          <div key={p.name} className="flex justify-between text-sm py-0.5">
                            <span>{p.name} · {p.sessions} buổi</span>
                            <span className={pct > 10 ? 'text-rose-600 font-medium' : 'text-gray-600'}>{fmtMoney(p.actualCost)} ({pct > 0 ? '+' : ''}{pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ),
              }} />
          </Panel>
        )}

        {tab === 'trace' && (!q.trim() ? (
          <div className="bg-white dark:bg-gray-900 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center text-sm text-gray-500">
            Nhập số lô hoặc tên thuốc để xem lô đó đã dùng cho những khách nào và còn nằm ở kho nào — dùng khi khách phản ứng sau tiêm hoặc nhà sản xuất thu hồi lô.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Panel title={`Đã dùng / bán cho khách (${trace.used.length})`}>
              <Table<Move & { key: string }> rowKey="key" size="small" columns={usedColumns} dataSource={trace.used} scroll={{ x: 1000 }} pagination={{ pageSize: 20, hideOnSinglePage: true }} />
            </Panel>
            <Panel title={`Còn tồn trong kho (${trace.stock.length} lô)`}>
              <Table<Lot> rowKey="key" size="small" pagination={false} dataSource={trace.stock} scroll={{ x: 760 }} columns={[
                { title: 'Mặt hàng', key: 'it', render: (_, l) => <ItemCell item={itemById(s, l.itemId)} /> },
                { title: 'Lô', dataIndex: 'lotNo', key: 'lot', render: v => <span className="font-mono text-xs">{v}</span> },
                { title: 'Hạn dùng', key: 'e', render: (_, l) => <ExpiryText expiry={l.expiry} now={now} /> },
                { title: 'Kho', key: 'w', render: (_, l) => whById(s, l.warehouseId)?.name },
                { title: 'Số lượng', key: 'q', align: 'right', render: (_, l) => <b>{fmtQty(itemById(s, l.itemId), l.qty)}</b> },
              ]} />
            </Panel>
          </div>
        ))}
      </div>
    </Layout>
  );
}
